import { useState } from "react";
import { useContent } from "@/lib/content-context";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import {
    ArrowRight, ChevronDown, Clock, ShieldCheck, Phone, CheckCircle2,
    Settings, Layers, Coffee, Plane, Sparkles, Heart, Star, MessageCircle,
} from "lucide-react";

const STRENGTH_ICONS = [Settings, Layers, Coffee, Plane, Sparkles, Heart];
const REASSURE_ICONS = { Clock, ShieldCheck, Phone };

const INVESTMENT_OPTIONS = ["75 Lakhs", "1 Crore", "1.5 Crore", "2 Crore+"];
const SOURCE_OPTIONS = ["Instagram", "Word of Mouth", "Google Search", "Visited a Cafe", "Other"];
const TYPE_OPTIONS = ["Franchise", "Location", "Other"];

const scrollToForm = () => {
    document.getElementById("partner-form")?.scrollIntoView({ behavior: "smooth" });
};

export default function Partner() {
    const { content } = useContent();
    const [form, setForm] = useState({
        name: "", phone: "", email: "", city: "",
        interest: "Franchise", investment: "75 Lakhs",
        message: "", source: "Instagram", consent: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    if (!content) return null;
    const p = content.partner || {};
    const opp = p.opportunity || {};
    const strengths = p.strengths || {};
    const models = p.models || {};
    const heart = p.heart || {};
    const reassurance = p.reassurance || [];

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.email || !form.city) {
            toast.error("Please fill in all required fields."); return;
        }
        if (!form.consent) {
            toast.error("Please accept the consent checkbox."); return;
        }
        setSubmitting(true);
        try {
            await api.post("/partner", form);
            setDone(true);
            setForm({ name: "", phone: "", email: "", city: "", interest: "Franchise", investment: "75 Lakhs", message: "", source: "Instagram", consent: false });
        } catch {
            toast.error("Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-cream-light" data-testid="partner-page">
            <SEO title="Partner With Us" description={p.intro} image={fileUrl(p.hero_image)} path="/partner" />

            {/* HERO */}
            <section
                className="relative w-full flex items-center justify-center text-center px-6"
                style={{ minHeight: "65vh", paddingTop: "112px" }}
                data-testid="partner-hero"
            >
                <div className="absolute inset-0 overflow-hidden">
                    <img src={fileUrl(p.hero_image)} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: "rgba(26,15,0,0.45)" }} />
                </div>
                <div className="relative z-10 max-w-3xl py-20 md:py-28">
                    <Reveal>
                        <div className="tracked text-gold text-[10px] mb-7">{p.label}</div>
                        <h1
                            className="font-editorial font-bold text-cream leading-[0.98] mb-7"
                            style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.6rem)" }}
                            data-testid="partner-title"
                        >
                            {p.title}
                        </h1>
                        <p className="font-body text-cream/85 max-w-xl mx-auto text-base md:text-lg leading-[1.75]">
                            {p.intro}
                        </p>
                        <button
                            onClick={scrollToForm}
                            className="btn-pill btn-pill-cream mt-10"
                            data-testid="partner-cta-hero"
                        >
                            {p.cta_label || "Start The Conversation"} <ChevronDown size={14} />
                        </button>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 1 — THE OPPORTUNITY */}
            <section
                className="relative px-6 md:px-12 py-24 md:py-32"
                style={{ backgroundColor: "#faf6ef" }}
                data-testid="partner-opportunity"
            >
                <div className="max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <Reveal className="lg:col-span-7">
                        <div className="tracked text-coffee/50 text-[10px] mb-6">The Opportunity</div>
                        <h2
                            className="font-editorial font-bold text-coffee leading-[1.02] mb-10"
                            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
                        >
                            {opp.heading}
                        </h2>
                        <div className="space-y-5 font-body text-coffee/85 text-base md:text-[17px] leading-[1.85] max-w-[58ch]">
                            {(opp.paragraphs || []).map((para, i) => <p key={i}>{para}</p>)}
                        </div>
                        {opp.quote && (
                            <blockquote className="mt-12 pl-6 border-l-2 border-gold max-w-xl">
                                <p
                                    className="italic text-2xl md:text-[28px] leading-[1.35]"
                                    style={{ color: "#8a6b3d", fontFamily: '"Open Sans", "Helvetica Neue", sans-serif', fontWeight: 600 }}
                                >
                                    "{opp.quote}"
                                </p>
                            </blockquote>
                        )}
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden bg-coffee/10">
                            <img src={fileUrl(opp.image || p.hero_image)} alt="Cafe Hideout outlet" className="w-full h-full object-cover" />
                        </div>
                    </Reveal>
                </div>

                {/* Gold stats strip */}
                <Reveal delay={0.15} className="max-w-[1320px] mx-auto mt-20 md:mt-24">
                    <div
                        className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0"
                        style={{ borderTop: "1px solid rgba(200,169,110,0.4)", borderBottom: "1px solid rgba(200,169,110,0.4)", borderColor: "rgba(200,169,110,0.4)" }}
                        data-testid="partner-stats-strip"
                    >
                        {(opp.stats || []).map((s, i) => (
                            <div key={i} className="px-6 py-8 md:py-10 text-center" data-testid={`partner-stat-${i}`}>
                                <div
                                    className="font-editorial font-bold leading-none"
                                    style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", color: "#b88d52" }}
                                >
                                    {s.value}
                                </div>
                                <div className="tracked text-coffee/60 text-[10px] mt-4">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </Reveal>
            </section>

            {/* SECTION 2 — BRAND STRENGTHS */}
            <section
                className="relative overflow-hidden px-6 md:px-12 py-24 md:py-32"
                style={{ backgroundColor: "#1a0f00" }}
                data-testid="partner-strengths"
            >
                <div className="grain-light pointer-events-none" />
                <div className="relative max-w-[1320px] mx-auto">
                    <Reveal>
                        <div className="tracked text-gold text-[10px] mb-6">Why Hideout</div>
                        <h2
                            className="font-editorial font-bold text-cream leading-[1.05] max-w-3xl"
                            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
                        >
                            {strengths.heading}
                        </h2>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mt-16 md:mt-20">
                        {(strengths.cards || []).map((c, i) => {
                            const Icon = STRENGTH_ICONS[i % STRENGTH_ICONS.length];
                            return (
                                <Reveal key={i} delay={i * 0.05}>
                                    <div
                                        className="group h-full bg-coffee-dark/40 border border-cream/10 hover:border-gold transition-all duration-500 p-8 md:p-9"
                                        data-testid={`strength-card-${i}`}
                                    >
                                        <Icon size={26} strokeWidth={1.2} className="text-gold mb-7 transition-transform duration-500 group-hover:-translate-y-1" />
                                        <h3 className="font-editorial font-bold text-gold text-xl md:text-[22px] mb-3">{c.title}</h3>
                                        <p className="font-body font-light text-cream/70 text-[14.5px] leading-[1.7]">{c.body}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 3 — PARTNERSHIP MODELS */}
            <section
                className="px-6 md:px-12 py-24 md:py-32 bg-cream-light"
                data-testid="partner-models"
            >
                <div className="max-w-[1280px] mx-auto">
                    <Reveal className="text-center max-w-2xl mx-auto mb-16">
                        <div className="tracked text-coffee/50 text-[10px] mb-6">Partnership Models</div>
                        <h2 className="font-editorial font-bold text-coffee leading-[1.05]"
                            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                            {models.heading}
                        </h2>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        {(models.cards || []).map((m, i) => (
                            <Reveal key={i} delay={i * 0.08}>
                                <div
                                    className="group relative h-full bg-white border border-coffee/10 hover:border-coffee p-8 md:p-10 transition-all duration-500"
                                    data-testid={`model-card-${i}`}
                                >
                                    {m.badge && (
                                        <div className="absolute -top-3 left-8 inline-flex items-center gap-1.5 px-3 py-1.5 bg-coffee text-gold tracked text-[10px]">
                                            <Star size={11} className="fill-gold" /> {m.badge}
                                        </div>
                                    )}
                                    <div className="font-mono tracked text-gold text-[10px] mb-4">0{i + 1}</div>
                                    <h3 className="font-editorial font-bold text-coffee text-2xl md:text-3xl mb-4">{m.title}</h3>
                                    <p className="font-body text-coffee/75 text-[15px] leading-[1.75] mb-8 min-h-[80px]">{m.body}</p>
                                    <button
                                        onClick={scrollToForm}
                                        className="btn-pill btn-pill-coffee"
                                        data-testid={`model-cta-${i}`}
                                    >
                                        {m.cta} <ArrowRight size={14} />
                                    </button>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 4 — THE HEART OF IT */}
            <section
                className="relative overflow-hidden px-6 md:px-12 py-24 md:py-32"
                style={{ backgroundColor: "#1a0f00" }}
                data-testid="partner-heart"
            >
                <div className="grain-light pointer-events-none" />

                {/* Decorative HIDEOUT watermark */}
                <div
                    aria-hidden
                    className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center font-editorial font-bold leading-none pointer-events-none select-none whitespace-nowrap"
                    style={{ fontSize: "clamp(8rem, 18vw, 17rem)", color: "#d4a574", opacity: 0.05, letterSpacing: "0.05em" }}
                >
                    HIDEOUT
                </div>

                <div className="relative max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

                    {/* Left — editorial image with gold frame */}
                    <Reveal className="lg:col-span-5 order-2 lg:order-1">
                        <div className="relative">
                            {/* Offset gold frame */}
                            <div
                                aria-hidden
                                className="absolute -inset-3 md:-inset-4 pointer-events-none"
                                style={{ border: "1px solid rgba(212,165,116,0.35)" }}
                            />
                            <div className="relative aspect-[4/5] overflow-hidden bg-coffee-dark">
                                <img
                                    src={fileUrl(heart.image || "https://images.pexels.com/photos/4109743/pexels-photo-4109743.jpeg")}
                                    alt="The hands that built Hideout"
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                    style={{ filter: "saturate(0.85) contrast(1.05)" }}
                                />
                                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(26,15,0,0) 50%, rgba(26,15,0,0.55) 100%)" }} />
                            </div>
                            {/* Caption tag */}
                            <div className="absolute -bottom-4 -right-4 md:-bottom-5 md:-right-5 px-4 py-3 bg-coffee" style={{ border: "1px solid rgba(212,165,116,0.5)" }}>
                                <div className="tracked text-gold text-[10px]">Est. 2018</div>
                                <div className="font-editorial text-cream text-base mt-0.5">A Hideout, not a chain.</div>
                            </div>
                        </div>
                    </Reveal>

                    {/* Right — quote + text */}
                    <div className="lg:col-span-7 order-1 lg:order-2">
                        {/* Decorative top mark */}
                        <Reveal>
                            <div className="flex items-center gap-3 mb-8">
                                <span className="h-px w-12 bg-gold" />
                                <Coffee size={18} strokeWidth={1.3} className="text-gold" />
                                <span className="tracked text-gold text-[10px]">The Heart Of It</span>
                            </div>
                        </Reveal>

                        <Reveal delay={0.08}>
                            <blockquote className="relative">
                                {/* Big quotation mark */}
                                <span
                                    aria-hidden
                                    className="absolute -left-2 -top-8 md:-left-4 md:-top-12 font-editorial font-bold leading-none select-none pointer-events-none"
                                    style={{ fontSize: "clamp(5rem, 9vw, 8rem)", color: "#d4a574", opacity: 0.18 }}
                                >
                                    "
                                </span>
                                <p
                                    className="relative italic leading-[1.32]"
                                    style={{
                                        color: "#d4a574",
                                        fontFamily: '"Open Sans", "Helvetica Neue", sans-serif',
                                        fontWeight: 600,
                                        fontSize: "clamp(1.4rem, 2.6vw, 2rem)",
                                    }}
                                >
                                    {heart.main_quote}
                                </p>
                            </blockquote>
                        </Reveal>

                        <Reveal delay={0.16}>
                            <div className="mt-12 space-y-6 font-body text-cream/75 text-base md:text-[17px] leading-[1.85] max-w-[58ch]">
                                {(heart.paragraphs || []).map((para, i) => <p key={i}>{para}</p>)}
                            </div>
                        </Reveal>

                        {heart.closing_line && (
                            <Reveal delay={0.22}>
                                <div className="mt-12 pt-10" style={{ borderTop: "1px solid rgba(212,165,116,0.3)" }}>
                                    <div
                                        className="font-editorial font-bold text-cream mb-5"
                                        style={{ fontSize: "clamp(1.3rem, 2.4vw, 1.85rem)" }}
                                    >
                                        {heart.closing_line}
                                    </div>
                                    <button
                                        onClick={scrollToForm}
                                        className="btn-pill btn-pill-dark"
                                        data-testid="partner-heart-cta"
                                    >
                                        Start The Conversation <ArrowRight size={14} />
                                    </button>
                                </div>
                            </Reveal>
                        )}
                    </div>
                </div>
            </section>

            {/* SECTION 5 — INQUIRY FORM */}
            <section
                id="partner-form"
                className="relative px-6 md:px-12 py-24 md:py-32"
                style={{ backgroundColor: "#faf6ef" }}
            >
                <div className="max-w-[820px] mx-auto">
                    <Reveal className="text-center mb-12">
                        <div className="tracked text-coffee/50 text-[10px] mb-6">Partner Inquiry</div>
                        <h2 className="font-editorial font-bold text-coffee leading-[1.05]"
                            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                            {p.form_heading}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="bg-white p-8 md:p-12 border border-gold/40 shadow-[0_30px_80px_-40px_rgba(184,141,82,0.25)]">
                            {done ? (
                                <div className="text-center py-8" data-testid="partner-success">
                                    <CheckCircle2 size={42} strokeWidth={1.2} className="text-gold mx-auto mb-6" />
                                    <div className="tracked text-coffee/50 text-[10px] mb-4">Inquiry Received</div>
                                    <h3 className="font-editorial font-bold text-coffee text-3xl md:text-4xl mb-4">
                                        Thank you.
                                    </h3>
                                    <p className="font-body text-coffee/70 max-w-md mx-auto text-base md:text-lg leading-relaxed">
                                        We'll be in touch within 48 hours. In the meantime, keep an eye on your inbox.
                                    </p>
                                    <button onClick={() => setDone(false)} className="btn-pill btn-pill-coffee mt-8" data-testid="partner-submit-another">
                                        Submit Another <ArrowRight size={14} />
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-8" data-testid="partner-form-element">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <Field label="Full Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="partner-name" required />
                                        <Field label="Phone Number *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="partner-phone" required />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <Field label="Email Address *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="partner-email" required />
                                        <Field label="City / Location *" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="partner-city" required />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <SelectField label="Partnership Type *" value={form.interest} onChange={(v) => setForm({ ...form, interest: v })} options={TYPE_OPTIONS} testid="partner-interest" />
                                        <SelectField label="Amount Ready to Invest *" value={form.investment} onChange={(v) => setForm({ ...form, investment: v })} options={INVESTMENT_OPTIONS} testid="partner-investment" />
                                    </div>
                                    <Field label="Tell us about your interest / property" textarea value={form.message} onChange={(v) => setForm({ ...form, message: v })} testid="partner-message" />
                                    <SelectField label="How did you hear about us?" value={form.source} onChange={(v) => setForm({ ...form, source: v })} options={SOURCE_OPTIONS} testid="partner-source" />

                                    <label className="flex items-start gap-3 cursor-pointer pt-2" data-testid="partner-consent-wrap">
                                        <input
                                            type="checkbox"
                                            checked={form.consent}
                                            onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                                            data-testid="partner-consent"
                                            className="mt-1 accent-coffee"
                                            required
                                        />
                                        <span className="font-body text-coffee/70 text-sm leading-relaxed">
                                            I'm okay with Cafe Hideout contacting me regarding this inquiry. My details will not be shared with third parties.
                                        </span>
                                    </label>

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="btn-pill btn-pill-coffee w-full justify-center disabled:opacity-50 mt-2"
                                        data-testid="partner-submit"
                                        style={{ background: "#d4a574", color: "#1a0f00", borderColor: "#b88d52" }}
                                    >
                                        {submitting ? "Sending…" : "Submit My Inquiry"} <ArrowRight size={14} />
                                    </button>

                                    {/* "or" divider */}
                                    <div className="relative pt-4 pb-1">
                                        <div className="absolute inset-x-0 top-1/2 flex items-center" aria-hidden>
                                            <div className="w-full" style={{ borderTop: "1px solid rgba(26,15,0,0.12)" }} />
                                        </div>
                                        <div className="relative flex justify-center">
                                            <span className="px-4 tracked text-coffee/45 text-[10px] bg-white">Or</span>
                                        </div>
                                    </div>

                                    <a
                                        href="https://wa.me/917292959595?text=Hi%20Cafe%20Hideout!%20I%27d%20like%20to%20explore%20a%20partnership."
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="partner-whatsapp-cta"
                                        className="group w-full inline-flex items-center justify-center gap-3 px-6 py-4 transition-all duration-300 hover:gap-4"
                                        style={{
                                            backgroundColor: "rgba(245,230,200,0.4)",
                                            border: "1px solid rgba(200,169,110,0.55)",
                                            color: "#1a0f00",
                                        }}
                                    >
                                        <MessageCircle size={17} strokeWidth={1.5} className="text-gold transition-transform duration-300 group-hover:-translate-y-0.5" />
                                        <span className="font-editorial font-medium text-base md:text-lg">Chat with us on WhatsApp</span>
                                        <span className="tracked text-coffee/45 text-[10px] hidden sm:inline">+91 72929 59595</span>
                                    </a>
                                </form>
                            )}
                        </div>
                    </Reveal>

                    {/* Reassurance strip */}
                    {reassurance.length > 0 && (
                        <Reveal delay={0.2}>
                            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-3 text-center" data-testid="partner-reassurance">
                                {reassurance.map((r, i) => {
                                    const Icon = REASSURE_ICONS[r.icon] || ShieldCheck;
                                    return (
                                        <div key={i} className="flex items-center justify-center gap-2.5">
                                            <Icon size={16} strokeWidth={1.4} className="text-gold" />
                                            <span className="tracked text-coffee/60 text-[10px]">{r.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </Reveal>
                    )}
                </div>
            </section>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", textarea = false, testid, required = false }) {
    return (
        <div>
            <label className="tracked text-coffee/60 text-[10px] block mb-3">{label}</label>
            {textarea ? (
                <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={4} data-testid={testid} required={required}
                    className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none resize-none" />
            ) : (
                <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} required={required}
                    className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none" />
            )}
        </div>
    );
}

function SelectField({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="tracked text-coffee/60 text-[10px] block mb-3">{label}</label>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none">
                {options.map((o) => <option key={o}>{o}</option>)}
            </select>
        </div>
    );
}
